<template>
  <dx-modal title="Detalhes da auditoria"
            :value="show"
            @input="$emit('close')"
            size="lg"
            id="modal-audit-detail">
    <div class="audit-detail">
      <div class="columns">
        <div class="column col-6 col-sm-12">
          <div class="audit-meta">
            <div class="audit-meta-row">
              <span class="audit-meta-label">Data e hora</span>
              <span class="audit-meta-value">{{ item.at | date('datetime') }}</span>
            </div>
            <div class="audit-meta-row">
              <span class="audit-meta-label">Ação</span>
              <span class="audit-meta-value">{{ actionName(item.action) }}</span>
            </div>
            <div class="audit-meta-row">
              <span class="audit-meta-label">Registro</span>
              <span class="audit-meta-value">{{ entityName(item.entity) }}</span>
            </div>
            <div class="audit-meta-row">
              <span class="audit-meta-label">Identificação</span>
              <span class="audit-meta-value">{{ item.label || '--' }}</span>
            </div>
            <div class="audit-meta-row">
              <span class="audit-meta-label">ID do registro</span>
              <span class="audit-meta-value audit-meta-id">{{ item.documentId || '--' }}</span>
            </div>
          </div>
        </div>
        <div class="column col-6 col-sm-12">
          <div class="audit-meta">
            <div class="audit-meta-row">
              <span class="audit-meta-label">Origem</span>
              <span class="audit-meta-value">{{ originLabel }}</span>
            </div>
            <div class="audit-meta-row">
              <span class="audit-meta-label">Dispositivo</span>
              <span class="audit-meta-value">{{ device }}</span>
            </div>
            <div class="audit-meta-row">
              <span class="audit-meta-label">IP</span>
              <span class="audit-meta-value">{{ ip }}</span>
            </div>
            <div class="audit-meta-row">
              <span class="audit-meta-label">Usuário responsável</span>
              <span class="audit-meta-value">{{ actorName }}</span>
            </div>
          </div>
        </div>
        <div class="column col-12" v-if="item.description">
          <div class="audit-meta">
            <div class="audit-meta-row">
              <span class="audit-meta-label">Descrição</span>
              <span class="audit-meta-value">{{ item.description }}</span>
            </div>
          </div>
        </div>
      </div>

      <template v-if="changes.length > 0">
        <div class="audit-section-title">Alterações</div>
        <table class="table audit-table">
          <thead>
          <tr>
            <th width="30%">Campo</th>
            <th width="35%">De</th>
            <th width="35%">Para</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(change, i) in changes" :key="i">
            <td>
              <div :class="fieldClass(change)">{{ fieldName(change) }}</div>
              <div class="audit-field-context" v-if="showContext(change)">
                {{ change.context }}
              </div>
            </td>
            <td class="audit-from">
              <audit-value :value="change.from"
                           :entity="item.entity"
                           :path="change.path"/>
            </td>
            <td class="audit-to">
              <audit-value :value="change.to"
                           :entity="item.entity"
                           :path="change.path"/>
            </td>
          </tr>
          </tbody>
        </table>
      </template>

      <template v-if="snapshot.length > 0">
        <div class="audit-section-title">Conteúdo do registro excluído</div>
        <table class="table audit-table">
          <thead>
          <tr>
            <th width="30%">Campo</th>
            <th>Valor</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(field, i) in snapshot" :key="i">
            <td :class="fieldClass({ path: field.key })">{{ fieldName({ path: field.key }) }}</td>
            <td>
              <audit-value :value="field.value"
                           :entity="item.entity"
                           :path="field.key"/>
            </td>
          </tr>
          </tbody>
        </table>
      </template>

      <template v-if="data.length > 0">
        <div class="audit-section-title">Informações do evento</div>
        <table class="table audit-table">
          <thead>
          <tr>
            <th width="30%">Campo</th>
            <th>Valor</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(field, i) in data" :key="i">
            <td class="audit-field">{{ field.key }}</td>
            <td><audit-value :value="field.value"/></td>
          </tr>
          </tbody>
        </table>
      </template>

      <div class="empty" v-if="isEmpty">
        <p class="empty-subtitle">
          Este registro não possui detalhamento de campos.
        </p>
      </div>
    </div>
    <template slot="footer">
      <button class="btn" @click="$emit('close')">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { getName as getEntityName } from '@/data/audit-entities';
import { getName as getActionName } from '@/data/audit-actions';
import { getName as getOriginName } from '@/data/audit-origins';
import { translate as translateField } from '@/data/audit-fields';
import auditValue from './Value.vue';

function toFields(value) {
  if (!value || typeof value !== 'object') return [];
  return Object.keys(value).map(key => ({ key, value: value[key] }));
}

export default {
  components: {
    auditValue,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  computed: {
    changes() {
      return this.item.changes || [];
    },
    snapshot() {
      return toFields(this.item.snapshot);
    },
    data() {
      return toFields(this.item.data);
    },
    isEmpty() {
      return this.changes.length === 0
        && this.snapshot.length === 0
        && this.data.length === 0;
    },
    actorName() {
      const { actor } = this.item;
      if (!actor) return '--';
      if (actor.name) return actor.name;
      return actor.system ? 'Sistema' : '--';
    },
    originLabel() {
      const { origin } = this.item;
      return origin && origin.source ? getOriginName(origin.source) : '--';
    },
    device() {
      const { actor } = this.item;
      return actor && actor.device ? actor.device : '--';
    },
    ip() {
      const { actor } = this.item;
      return actor && actor.ip ? actor.ip : '--';
    },
  },
  methods: {
    actionName: getActionName,
    entityName: getEntityName,
    translated(change) {
      return translateField(this.item.entity, change.path, change.context);
    },
    fieldName(change) {
      return this.translated(change) || change.path;
    },
    fieldClass(change) {
      return this.translated(change) ? null : 'audit-field';
    },
    showContext(change) {
      return !!change.context && !this.translated(change);
    },
  },
};
</script>

<style lang="scss">
  @import "./src/assets/scss/_variables.scss";
  #modal-audit-detail {
    .audit-meta {
      &-row {
        align-items: baseline;
        border-bottom: $border-width solid $border-color;
        display: flex;
        padding: $unit-2 0;
        &:last-child {
          border-bottom: 0;
        }
      }
      &-label {
        color: $gray-color-dark;
        flex: 0 0 8.5rem;
        font-size: $font-size-sm;
        text-transform: uppercase;
      }
      &-value {
        flex: 1 1 auto;
        font-weight: 500;
        word-break: break-word;
      }
      &-id {
        font-family: $mono-font-family;
        font-size: $font-size-sm;
        font-weight: 400;
      }
    }
    .audit-section-title {
      border-top: $border-width solid $border-color;
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin-top: $unit-4;
      padding-top: $unit-3;
      text-transform: uppercase;
    }
    .audit-table {
      td {
        vertical-align: top;
        word-break: break-word;
      }
      .audit-field {
        font-family: $mono-font-family;
        font-size: $font-size-sm;
      }
      .audit-field-context {
        color: $gray-color-dark;
        font-size: $font-size-sm;
      }
      .audit-from {
        color: $gray-color-dark;
      }
      .audit-to {
        font-weight: 500;
      }
    }
  }
</style>
